import type {
    BoxShadowValue,
    ViewStyle
} from "react-native";
import {
    withAlpha
} from "../../utils/color";

export type ElevationLevelType = keyof NCoreUIKit.Elevations;

export type SurfaceType = "popover" | "dialog" | "sheet" | "toast" | "card";

export type SurfaceLevelType = {
    borderColor: keyof NCoreUIKit.BorderContentColors;
    level: ElevationLevelType;
};

export type ElevationConstantType = {
    elevations: NCoreUIKit.Elevations;
    level: ElevationLevelType;
    shadowAlpha: number;
    shadowColor: string;
};

export type SurfaceConstantType = {
    elevations: NCoreUIKit.Elevations;
    colors: NCoreUIKit.ThemeTokens;
    borders: NCoreUIKit.Borders;
    level?: ElevationLevelType;
    surface: SurfaceType;
    shadowAlpha: number;
};

export const SURFACE_CONTAINER_COLOR: keyof NCoreUIKit.ContainerContentColors = "raised";

export const SURFACE_LEVELS: Record<SurfaceType, SurfaceLevelType> = {
    dialog: {
        borderColor: "overlay",
        level: "level4"
    },
    popover: {
        borderColor: "overlay",
        level: "level2"
    },
    sheet: {
        borderColor: "overlay",
        level: "level3"
    },
    toast: {
        borderColor: "overlay",
        level: "level3"
    },
    card: {
        borderColor: "subtle",
        level: "level1"
    }
};

export const getElevationLayers = ({
    shadowAlpha,
    shadowColor,
    elevations,
    level
}: ElevationConstantType): Array<BoxShadowValue> => {
    const layers = elevations[level] ?? [];

    return layers.map((layer) => ({
        color: withAlpha({
            alpha: shadowAlpha * layer.alpha,
            color: shadowColor
        }),
        blurRadius: layer.blur,
        offsetY: layer.offset,
        offsetX: 0
    }));
};

export const getElevationStyle = ({
    shadowAlpha,
    shadowColor,
    elevations,
    level
}: ElevationConstantType): ViewStyle => {
    return {
        boxShadow: getElevationLayers({
            shadowAlpha,
            shadowColor,
            elevations,
            level
        })
    };
};

export const getSurfaceStyle = ({
    shadowAlpha,
    elevations,
    borders,
    surface,
    colors,
    level
}: SurfaceConstantType): ViewStyle => {
    const currentSurface = SURFACE_LEVELS[surface];

    return {
        backgroundColor: colors.content.container[SURFACE_CONTAINER_COLOR],
        borderColor: colors.content.border[currentSurface.borderColor],
        boxShadow: getElevationLayers({
            level: level ?? currentSurface.level,
            shadowColor: colors.system.shadow,
            shadowAlpha,
            elevations
        }),
        borderWidth: borders.line
    };
};
